import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  FileSpreadsheet, 
  FileText, 
  Calendar,
  Clock,
  User,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  X,
  Download,
  Trash2,
  ChevronDown,
  ChevronRight,
  Pencil,
  ShoppingBag,
  Wallet,
  CreditCard,
  RefreshCw,
  Banknote,
  RotateCcw
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format currency helper for PaymentMethodBadge
const formatCurrencySimple = (value) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0
  }).format(value || 0);
};

// Payment method badge with split details popover for mixto (click to open)
const PaymentMethodBadge = ({ sale }) => {
  const method = sale.payment_method;
  const split = sale.payment_split;
  
  const getBadgeStyle = (m) => {
    switch(m) {
      case 'efectivo': return 'bg-emerald-600/20 text-emerald-400';
      case 'tarjeta': return 'bg-blue-600/20 text-blue-400';
      case 'mixto': return 'bg-purple-600/20 text-purple-400 cursor-pointer hover:bg-purple-600/30';
      default: return 'bg-amber-600/20 text-amber-400';
    }
  };
  
  if (method === 'mixto' && split) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <span className={`px-2 py-0.5 rounded text-xs ${getBadgeStyle(method)}`}>
            mixto ▾
          </span>
        </PopoverTrigger>
        <PopoverContent side="top" className="bg-zinc-900 border-zinc-700 p-3 w-auto">
          <div className="space-y-1.5 text-xs">
            <p className="font-semibold text-white mb-2">Desglose del Pago:</p>
            {split.efectivo > 0 && (
              <div className="flex items-center justify-between gap-4">
                <span className="flex items-center gap-1.5 text-emerald-400">
                  <Banknote size={12} /> Efectivo
                </span>
                <span className="font-mono text-white">{formatCurrencySimple(split.efectivo)}</span>
              </div>
            )}
            {split.tarjeta > 0 && (
              <div className="flex items-center justify-between gap-4">
                <span className="flex items-center gap-1.5 text-blue-400">
                  <CreditCard size={12} /> Tarjeta
                </span>
                <span className="font-mono text-white">{formatCurrencySimple(split.tarjeta)}</span>
              </div>
            )}
            {split.deuda > 0 && (
              <div className="flex items-center justify-between gap-4">
                <span className="flex items-center gap-1.5 text-amber-400">
                  <Clock size={12} /> Deuda
                </span>
                <span className="font-mono text-white">{formatCurrencySimple(split.deuda)}</span>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }
  
  return (
    <span className={`px-2 py-0.5 rounded text-xs ${getBadgeStyle(method)}`}>
      {method}
    </span>
  );
};

// Helper to get first day of current month
const getFirstDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
};

// Helper to get last day of current month
const getLastDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
};

const Reports = () => {
  const { isAdmin } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteType, setDeleteType] = useState(null); // 'session', 'sale', 'expense'
  const [expandedSession, setExpandedSession] = useState(null);
  const [sessionDetails, setSessionDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [editSale, setEditSale] = useState(null);
  
  // Refund modal
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundingSale, setRefundingSale] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundProcessing, setRefundProcessing] = useState(false);
  
  // Filters - date range (default: current month)
  const [startDate, setStartDate] = useState(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState(getLastDayOfMonth());
  const [selectedUser, setSelectedUser] = useState('');
  
  // Report Modal
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportData, setReportData] = useState(null);
  
  // Migration state (admin only)
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [showMigrationResult, setShowMigrationResult] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedUser]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { 
        start_date: startDate,
        end_date: endDate
      };
      if (selectedUser) params.user_id = selectedUser;

      const [sessionsRes, usersRes] = await Promise.all([
        axios.get(`${API}/cash-session/history`, { params }),
        axios.get(`${API}/users`).catch(() => ({ data: [] }))
      ]);

      setSessions(sessionsRes.data.sessions || []);
      setUsers(usersRes.data || []);
      setSummary(sessionsRes.data.summary || null);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionDetails = async (sessionId) => {
    if (expandedSession === sessionId) {
      setExpandedSession(null);
      setSessionDetails(null);
      return;
    }
    
    setLoadingDetails(true);
    setExpandedSession(sessionId);
    try {
      const response = await axios.get(`${API}/sales/by-session/${sessionId}`);
      setSessionDetails(response.data);
    } catch (error) {
      toast.error('Error al cargar operaciones');
      setExpandedSession(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await axios.delete(`${API}/cash-session/${sessionId}`);
      toast.success('Registro de caja eliminado');
      setDeleteConfirm(null);
      setDeleteType(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Open refund modal
  const openRefundModal = (sale) => {
    setRefundingSale(sale);
    setRefundReason('');
    setShowRefundModal(true);
  };

  // Process refund
  const handleRefundSale = async () => {
    if (!refundingSale) return;
    setRefundProcessing(true);
    try {
      await axios.post(`${API}/sales/${refundingSale.id}/refund`, {
        reason: refundReason || null
      });
      toast.success('Devolución procesada correctamente');
      setShowRefundModal(false);
      setRefundingSale(null);
      setRefundReason('');
      if (expandedSession) {
        fetchSessionDetails(expandedSession);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar devolución');
    } finally {
      setRefundProcessing(false);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      await axios.delete(`${API}/expenses/${expenseId}`);
      toast.success('Gasto eliminado');
      setDeleteConfirm(null);
      setDeleteType(null);
      if (expandedSession) {
        fetchSessionDetails(expandedSession);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar gasto');
    }
  };

  const handleDeleteDebtPayment = async (paymentId) => {
    try {
      await axios.delete(`${API}/debt-payments/${paymentId}`);
      toast.success('Cobro eliminado y deuda restaurada');
      setDeleteConfirm(null);
      setDeleteType(null);
      if (expandedSession) {
        fetchSessionDetails(expandedSession);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar cobro');
    }
  };

  // Admin: Recalculate closed sessions to fix historical data
  const handleRecalculateSessions = async () => {
    setMigrationLoading(true);
    try {
      const response = await axios.post(`${API}/admin/recalculate-closed-sessions`);
      setMigrationResult(response.data);
      setShowMigrationResult(true);
      toast.success(`Migración completada: ${response.data.updated_count} sesiones actualizadas`);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al recalcular sesiones');
    } finally {
      setMigrationLoading(false);
    }
  };

  const handleUpdateSale = async () => {
    if (!editSale) return;
    try {
      await axios.put(`${API}/sales/${editSale.id}`, {
        customer_name: editSale.customer_name,
        notes: editSale.notes,
        discount_percent: editSale.discount_percent
      });
      toast.success('Venta actualizada');
      setEditSale(null);
      if (expandedSession) {
        fetchSessionDetails(expandedSession);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const generateReport = async (type) => {
    try {
      const params = { 
        start_date: startDate,
        end_date: endDate
      };
      if (selectedUser) params.user_id = selectedUser;

      let data, title;
      
      if (type === 'cierres') {
        const response = await axios.get(`${API}/cash-session/history`, { params });
        data = {
          sessions: response.data.sessions,
          summary: response.data.summary
        };
        title = 'Reporte de Cierres de Caja';
      } else if (type === 'ventas') {
        const response = await axios.get(`${API}/sales`, { params });
        data = { sales: response.data };
        title = 'Reporte de Ventas';
      } else if (type === 'gastos') {
        const response = await axios.get(`${API}/expenses`, { params });
        data = { expenses: response.data };
        title = 'Reporte de Gastos y Retiros';
      }

      // Format period string
      const startFormatted = new Date(startDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
      const endFormatted = new Date(endDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });

      setReportData({
        type,
        title,
        data,
        period: `${startFormatted} - ${endFormatted}`
      });
      setShowReportModal(true);
    } catch (error) {
      toast.error('Error al generar reporte');
    }
  };

  const exportToExcel = () => {
    if (!reportData) return;

    let wsData = [];
    
    if (reportData.type === 'cierres') {
      wsData = reportData.data.sessions.map(s => ({
        'Usuario': s.user_name,
        'Fecha': formatDate(s.opened_at),
        'Apertura': formatTime(s.opened_at),
        'Cierre': formatTime(s.closed_at),
        'Facturado': s.total_sales,
        'Diferencia': s.difference
      }));
    } else if (reportData.type === 'ventas') {
      wsData = reportData.data.sales.map(s => ({
        'Fecha': formatDateTime(s.created_at),
        'Cliente': s.customer_name || 'N/A',
        'Vendedor': s.seller_name,
        'Productos': (s.items || []).map(i => `${i.item_name} x${i.quantity}`).join(' | '),
        'Total': s.total,
        'Método': s.payment_method
      }));
    } else if (reportData.type === 'gastos') {
      wsData = reportData.data.expenses.map(e => ({
        'Fecha': formatDateTime(e.created_at),
        'Tipo': e.type === 'expense' ? 'Gasto' : 'Retiro',
        'Categoría': e.category,
        'Monto': e.amount,
        'Descripción': e.description
      }));
    }

    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reporte');
    XLSX.writeFile(wb, `${reportData.title.replace(/ /g, '_')}.xlsx`);
  };

  const exportToPDF = () => {
    if (!reportData) return;

    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(reportData.title, 14, 20);
    doc.setFontSize(10);
    doc.text(`Período: ${reportData.period}`, 14, 28);

    let tableData = [];
    let columns = [];

    if (reportData.type === 'cierres') {
      columns = ['Usuario', 'Fecha', 'Apertura', 'Cierre', 'Facturado', 'Diferencia'];
      tableData = reportData.data.sessions.map(s => [
        s.user_name,
        formatDate(s.opened_at),
        formatTime(s.opened_at),
        formatTime(s.closed_at),
        formatCurrency(s.total_sales),
        formatCurrency(s.difference)
      ]);
    } else if (reportData.type === 'ventas') {
      columns = ['Fecha', 'Cliente', 'Vendedor', 'Productos', 'Total', 'Método'];
      tableData = reportData.data.sales.map(s => [
        formatDateTime(s.created_at),
        s.customer_name || 'N/A',
        s.seller_name,
        (s.items || []).map(i => `${i.item_name} x${i.quantity}`).join(' | '),
        formatCurrency(s.total),
        s.payment_method
      ]);
    } else if (reportData.type === 'gastos') {
      columns = ['Fecha', 'Tipo', 'Categoría', 'Monto', 'Descripción'];
      tableData = reportData.data.expenses.map(e => [
        formatDateTime(e.created_at),
        e.type === 'expense' ? 'Gasto' : 'Retiro',
        e.category,
        formatCurrency(e.amount),
        e.description
      ]);
    }

    doc.autoTable({
      head: [columns],
      body: tableData,
      startY: 35,
      theme: 'grid',
      styles: { fontSize: 8 }
    });

    doc.save(`${reportData.title.replace(/ /g, '_')}.pdf`);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-ES', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]" />
      </div>
    );
  }

  return (
    <div data-testid="reports-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-3xl font-black text-white">Historial de Cajas</h1>
            <p className="text-zinc-500 text-sm">Historial de cierres de caja y reportes</p>
          </div>
          <Button
            onClick={fetchData}
            variant="outline"
            size="sm"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-[#0A0A0A] border-zinc-800">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-zinc-400 text-sm">Desde:</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40 bg-zinc-900 border-zinc-800 text-white"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Label className="text-zinc-400 text-sm">Hasta:</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40 bg-zinc-900 border-zinc-800 text-white"
              />
            </div>

            {users.length > 0 && (
              <Select value={selectedUser || "all"} onValueChange={(v) => setSelectedUser(v === "all" ? "" : v)}>
                <SelectTrigger className="w-44 bg-zinc-900 border-zinc-800">
                  <User size={14} className="mr-2" />
                  <SelectValue placeholder="Usuario" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-zinc-800">
                  <SelectItem value="all">Todos</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <div className="flex-1" />

            <div className="flex gap-2">
              <Button
                data-testid="generate-cierres-report"
                onClick={() => generateReport('cierres')}
                variant="outline"
                size="sm"
                className="border-zinc-700"
              >
                Cierres de Caja
              </Button>
              <Button
                data-testid="generate-ventas-report"
                onClick={() => generateReport('ventas')}
                variant="outline"
                size="sm"
                className="border-zinc-700"
              >
                Ventas
              </Button>
              <Button
                data-testid="generate-gastos-report"
                onClick={() => generateReport('gastos')}
                variant="outline"
                size="sm"
                className="border-zinc-700"
              >
                Gastos y Retiros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-[#0A0A0A] border-zinc-800">
            <CardContent className="p-4">
              <p className="text-zinc-500 text-sm">Cierres</p>
              <p className="text-2xl font-bold text-white">{summary.total_sessions}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-zinc-800">
            <CardContent className="p-4">
              <p className="text-zinc-500 text-sm">Total Facturado (Neto)</p>
              <p className="text-2xl font-bold text-emerald-400">{formatCurrency(summary.total_net_sales ?? summary.total_sales)}</p>
              {summary.total_refunds > 0 && (
                <p className="text-xs text-red-400 mt-1">(-{formatCurrency(summary.total_refunds)} en devoluciones)</p>
              )}
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-zinc-800">
            <CardContent className="p-4">
              <p className="text-zinc-500 text-sm">Diferencias</p>
              <p className={`text-2xl font-bold ${summary.total_differences >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
                {formatCurrency(summary.total_differences)}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-zinc-800">
            <CardContent className="p-4">
              <p className="text-zinc-500 text-sm">Con Diferencia</p>
              <p className="text-2xl font-bold text-amber-400">{summary.sessions_with_differences}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sessions Table */}
      <Card className="bg-[#0A0A0A] border-zinc-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white">Historial de Cajas</CardTitle>
          {isAdmin() && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRecalculateSessions}
              disabled={migrationLoading}
              className="border-amber-600/50 text-amber-400 hover:bg-amber-600/10 hover:text-amber-300"
            >
              {migrationLoading ? (
                <>
                  <RefreshCw size={14} className="mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <RefreshCw size={14} className="mr-2" />
                  Recalcular Histórico
                </>
              )}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {sessions.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-400 w-8"></TableHead>
                    <TableHead className="text-zinc-400">Usuario</TableHead>
                    <TableHead className="text-zinc-400">Estado</TableHead>
                    <TableHead className="text-zinc-400">Fecha</TableHead>
                    <TableHead className="text-zinc-400">Hora Apertura</TableHead>
                    <TableHead className="text-zinc-400">Hora Cierre</TableHead>
                    <TableHead className="text-zinc-400 text-right">Monto Apertura</TableHead>
                    <TableHead className="text-zinc-400 text-right">Monto Cierre</TableHead>
                    <TableHead className="text-zinc-400 text-right">Facturado (Neto)</TableHead>
                    <TableHead className="text-zinc-400 text-right">Diferencia</TableHead>
                    {isAdmin() && <TableHead className="text-zinc-400 text-center">Acciones</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((session) => (
                    <>
                      <TableRow 
                        key={session.id} 
                        className={`border-zinc-800 hover:bg-zinc-900/50 cursor-pointer ${session.status === 'open' ? 'bg-emerald-900/10' : ''}`}
                        onClick={() => fetchSessionDetails(session.id)}
                      >
                        <TableCell className="text-zinc-400">
                          {expandedSession === session.id ? (
                            <ChevronDown size={18} className="text-[#FF3B30]" />
                          ) : (
                            <ChevronRight size={18} />
                          )}
                        </TableCell>
                        <TableCell className="text-white font-medium">{session.user_name}</TableCell>
                        <TableCell>
                          {session.status === 'open' ? (
                            <span className="px-2 py-0.5 rounded text-xs bg-emerald-600/20 text-emerald-400">ABIERTA</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded text-xs bg-zinc-600/20 text-zinc-400">Cerrada</span>
                          )}
                        </TableCell>
                        <TableCell className="text-zinc-300">{formatDate(session.opened_at)}</TableCell>
                        <TableCell className="text-zinc-400 font-mono text-sm">{formatTime(session.opened_at)}</TableCell>
                        <TableCell className="text-zinc-400 font-mono text-sm">
                          {session.status === 'open' ? '-' : formatTime(session.closed_at)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-amber-400">
                          {formatCurrency(session.initial_cash || 0)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-cyan-400">
                          {session.status === 'open' ? '-' : formatCurrency(session.counted_cash || 0)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          <span className="font-bold text-emerald-400">
                            {formatCurrency(session.net_sales ?? (session.total_sales - (session.refunds_total || 0)))}
                          </span>
                          {session.refunds_total > 0 && (
                            <span className="block text-xs text-red-400">
                              (-{formatCurrency(session.refunds_total)} dev.)
                            </span>
                          )}
                        </TableCell>
                        <TableCell className={`text-right font-mono font-bold ${
                          session.status === 'open' ? 'text-zinc-500' :
                          Math.abs(session.difference || 0) < 1 
                            ? 'text-zinc-400' 
                            : session.difference > 0 ? 'text-blue-400' : 'text-red-400'
                        }`}>
                          {session.status === 'open' ? '-' : (session.difference >= 0 ? '+' : '') + formatCurrency(session.difference)}
                        </TableCell>
                        {isAdmin() && (
                          <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                            {session.status !== 'open' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setDeleteConfirm(session); setDeleteType('session'); }}
                                className="text-red-400 hover:text-red-300 hover:bg-red-600/10"
                              >
                                <Trash2 size={16} />
                              </Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                      
                      {/* Expanded Session Details */}
                      {expandedSession === session.id && (
                        <TableRow className="bg-zinc-900/30">
                          <TableCell colSpan={isAdmin() ? 11 : 10} className="p-4">
                            {loadingDetails ? (
                              <div className="flex justify-center py-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#FF3B30]" />
                              </div>
                            ) : sessionDetails ? (
                              <div className="space-y-4">
                                {/* Sales */}
                                {sessionDetails.sales?.length > 0 && (
                                  <div>
                                    <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                                      <ShoppingBag size={16} className="text-emerald-400" />
                                      Ventas ({sessionDetails.sales.length})
                                    </h4>
                                    <div className="bg-zinc-900 rounded-sm overflow-hidden">
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="bg-zinc-800/50">
                                            <th className="text-left p-2 text-zinc-400">Hora</th>
                                            <th className="text-left p-2 text-zinc-400">Cliente</th>
                                            <th className="text-left p-2 text-zinc-400">Items</th>
                                            <th className="text-left p-2 text-zinc-400">Pago</th>
                                            <th className="text-right p-2 text-zinc-400">Subtotal</th>
                                            <th className="text-right p-2 text-zinc-400">Desc.</th>
                                            <th className="text-right p-2 text-zinc-400">Total</th>
                                            {isAdmin() && <th className="text-center p-2 text-zinc-400">Acciones</th>}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {sessionDetails.sales.map(sale => (
                                            <tr key={sale.id} className="border-t border-zinc-800">
                                              <td className="p-2 text-zinc-300">{formatTime(sale.created_at)}</td>
                                              <td className="p-2 text-white">{sale.customer_name || '-'}</td>
                                              <td className="p-2 text-zinc-400 max-w-xs">
                                                {sale.items && sale.items.length > 0 ? (
                                                  <div className="space-y-0.5">
                                                    {sale.items.map((item, idx) => (
                                                      <div key={idx} className="text-xs">
                                                        <span className="text-zinc-300">{item.quantity}x</span>{' '}
                                                        <span className="text-white">{item.item_name || item.name || 'Producto'}</span>{' '}
                                                        <span className="text-zinc-500">({formatCurrency(item.subtotal)})</span>
                                                      </div>
                                                    ))}
                                                  </div>
                                                ) : (
                                                  <span className="text-zinc-500">-</span>
                                                )}
                                              </td>
                                              <td className="p-2">
                                                <PaymentMethodBadge sale={sale} />
                                              </td>
                                              <td className="p-2 text-right font-mono text-zinc-300">
                                                {sale.refunded ? <s className="text-zinc-500">{formatCurrency(sale.subtotal)}</s> : formatCurrency(sale.subtotal)}
                                              </td>
                                              <td className="p-2 text-right font-mono">
                                                {(sale.descuento_monto || sale.discount_amount || 0) > 0 ? (
                                                  <span className="text-amber-400">
                                                    -{formatCurrency(sale.descuento_monto || sale.discount_amount)}
                                                    {(sale.descuento_porcentaje || sale.discount_percent) > 0 && (
                                                      <span className="text-xs ml-1">({sale.descuento_porcentaje || sale.discount_percent}%)</span>
                                                    )}
                                                  </span>
                                                ) : (
                                                  <span className="text-zinc-500">-</span>
                                                )}
                                              </td>
                                              <td className="p-2 text-right font-mono text-emerald-400">
                                                {sale.refunded ? <s className="text-zinc-500">{formatCurrency(sale.total_final || sale.total)}</s> : formatCurrency(sale.total_final || sale.total)}
                                              </td>
                                              {isAdmin() && (
                                                <td className="p-2 text-center">
                                                  {!sale.refunded ? (
                                                    <Button 
                                                      variant="ghost" 
                                                      size="sm" 
                                                      onClick={() => openRefundModal(sale)}
                                                      className="h-7 w-7 p-0 text-amber-400 hover:text-amber-300"
                                                      title="Devolver venta"
                                                    >
                                                      <RotateCcw size={14} />
                                                    </Button>
                                                  ) : (
                                                    <span className="text-xs text-red-400">Devuelta</span>
                                                  )}
                                                </td>
                                              )}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Refunds Section */}
                                {sessionDetails.refunds?.length > 0 && (
                                  <div>
                                    <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                                      <RotateCcw size={16} className="text-amber-400" />
                                      Devoluciones ({sessionDetails.refunds.length})
                                    </h4>
                                    <div className="bg-zinc-900 rounded-sm overflow-hidden">
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="bg-zinc-800/50">
                                            <th className="text-left p-2 text-zinc-400">Hora</th>
                                            <th className="text-left p-2 text-zinc-400">Items</th>
                                            <th className="text-left p-2 text-zinc-400">Razón</th>
                                            <th className="text-left p-2 text-zinc-400">Procesado por</th>
                                            <th className="text-right p-2 text-zinc-400">Monto</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {sessionDetails.refunds.map(refund => (
                                            <tr key={refund.id} className="border-t border-zinc-800">
                                              <td className="p-2 text-zinc-300">{formatTime(refund.created_at)}</td>
                                              <td className="p-2 text-white">
                                                {refund.items?.map(i => `${i.quantity}x ${i.item_name}`).join(', ') || '-'}
                                              </td>
                                              <td className="p-2 text-zinc-400">{refund.reason || '-'}</td>
                                              <td className="p-2 text-zinc-300">{refund.refunded_by_name}</td>
                                              <td className="p-2 text-right font-mono text-red-400">-{formatCurrency(refund.refund_amount)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}

                                {/* Expenses */}
                                {sessionDetails.expenses?.length > 0 && (
                                  <div>
                                    <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                                      <Wallet size={16} className="text-red-400" />
                                      Gastos y Retiros ({sessionDetails.expenses.length})
                                    </h4>
                                    <div className="bg-zinc-900 rounded-sm overflow-hidden">
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="bg-zinc-800/50">
                                            <th className="text-left p-2 text-zinc-400">Hora</th>
                                            <th className="text-left p-2 text-zinc-400">Tipo</th>
                                            <th className="text-left p-2 text-zinc-400">Categoría</th>
                                            <th className="text-left p-2 text-zinc-400">Descripción</th>
                                            <th className="text-right p-2 text-zinc-400">Monto</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {sessionDetails.expenses.map(expense => (
                                            <tr key={expense.id} className="border-t border-zinc-800">
                                              <td className="p-2 text-zinc-300">{formatTime(expense.created_at)}</td>
                                              <td className="p-2">
                                                <span className={`px-2 py-0.5 rounded text-xs ${
                                                  expense.type === 'expense' ? 'bg-red-600/20 text-red-400' : 'bg-purple-600/20 text-purple-400'
                                                }`}>
                                                  {expense.type === 'expense' ? 'Gasto' : 'Retiro'}
                                                </span>
                                              </td>
                                              <td className="p-2 text-zinc-400">{expense.category}</td>
                                              <td className="p-2 text-white">{expense.description}</td>
                                              <td className="p-2 text-right font-mono text-red-400">-{formatCurrency(expense.amount)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}

                                {/* Debt Payments */}
                                {sessionDetails.debt_payments?.length > 0 && (
                                  <div>
                                    <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                                      <CreditCard size={16} className="text-blue-400" />
                                      Cobros de Deudas ({sessionDetails.debt_payments.length})
                                    </h4>
                                    <div className="bg-zinc-900 rounded-sm overflow-hidden">
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="bg-zinc-800/50">
                                            <th className="text-left p-2 text-zinc-400">Hora</th>
                                            <th className="text-left p-2 text-zinc-400">Cliente</th>
                                            <th className="text-left p-2 text-zinc-400">Método</th>
                                            <th className="text-right p-2 text-zinc-400">Monto</th>
                                            {isAdmin() && <th className="text-center p-2 text-zinc-400">Acciones</th>}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {sessionDetails.debt_payments.map(payment => (
                                            <tr key={payment.id} className="border-t border-zinc-800">
                                              <td className="p-2 text-zinc-300">{formatTime(payment.date)}</td>
                                              <td className="p-2 text-white">{payment.customer_name || '-'}</td>
                                              <td className="p-2 text-zinc-400">{payment.method}</td>
                                              <td className="p-2 text-right font-mono text-blue-400">+{formatCurrency(payment.amount)}</td>
                                              {isAdmin() && (
                                                <td className="p-2 text-center">
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    data-testid={`delete-debt-payment-${payment.id}`}
                                                    onClick={() => { setDeleteConfirm(payment); setDeleteType('debtPayment'); }}
                                                    className="text-red-400 hover:text-red-300 hover:bg-red-600/10"
                                                  >
                                                    <Trash2 size={14} />
                                                  </Button>
                                                </td>
                                              )}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}

                                {!sessionDetails.sales?.length && !sessionDetails.expenses?.length && !sessionDetails.debt_payments?.length && !sessionDetails.refunds?.length && (
                                  <p className="text-zinc-500 text-center py-4">No hay operaciones registradas en esta caja</p>
                                )}
                              </div>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Clock size={48} className="mx-auto text-zinc-700 mb-4" />
              <p className="text-zinc-500">No hay cierres de caja en este período</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal - For sessions */}
      <Dialog open={!!deleteConfirm && deleteType === 'session'} onOpenChange={() => { setDeleteConfirm(null); setDeleteType(null); }}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="text-red-500" size={20} />
              Confirmar Eliminación
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-300">
              ¿Estás seguro de eliminar este cierre de caja?
            </p>
            <p className="text-red-400 text-sm mt-4">Esta acción no se puede deshacer.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteConfirm(null); setDeleteType(null); }} className="border-zinc-700">
              Cancelar
            </Button>
            <Button 
              onClick={() => {
                if (deleteType === 'session') handleDeleteSession(deleteConfirm?.id);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Debt Payment Confirmation Modal */}
      <Dialog open={!!deleteConfirm && deleteType === 'debtPayment'} onOpenChange={() => { setDeleteConfirm(null); setDeleteType(null); }}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-md z-[150]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="text-amber-500" size={20} />
              Eliminar Cobro de Deuda
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-300">
              ¿Estás seguro de eliminar este cobro de deuda?
            </p>
            {deleteConfirm && (
              <div className="mt-3 p-3 bg-zinc-900 rounded-sm">
                <p className="text-sm text-zinc-400">Cliente: <span className="text-white">{deleteConfirm.customer_name}</span></p>
                <p className="text-sm text-zinc-400">Monto: <span className="text-blue-400 font-mono">{formatCurrency(deleteConfirm.amount)}</span></p>
              </div>
            )}
            <p className="text-amber-400 text-sm mt-4">El monto será restaurado a la deuda pendiente.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteConfirm(null); setDeleteType(null); }} className="border-zinc-700">
              Cancelar
            </Button>
            <Button 
              onClick={() => handleDeleteDebtPayment(deleteConfirm?.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar y Restaurar Deuda
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Modal */}
      <Dialog open={showRefundModal} onOpenChange={setShowRefundModal}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-md z-[150]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <RotateCcw size={20} className="text-amber-400" />
              Confirmar Devolución
            </DialogTitle>
          </DialogHeader>
          
          {refundingSale && (
            <div className="space-y-4 py-4">
              {/* Sale details */}
              <div className="bg-zinc-900/50 p-4 rounded-sm">
                <p className="text-xs text-zinc-500 uppercase mb-2">Venta a Devolver</p>
                <div className="space-y-1">
                  {refundingSale.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-zinc-300">{item.quantity}x {item.item_name}</span>
                      <span className="text-zinc-400">{formatCurrency(item.subtotal)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-zinc-700 mt-3 pt-3 flex justify-between">
                  <span className="text-white font-medium">Total a Devolver:</span>
                  <span className="text-xl font-mono font-bold text-red-400">{formatCurrency(refundingSale.total)}</span>
                </div>
              </div>
              
              {/* Reason input */}
              <div>
                <Label className="text-zinc-400 mb-2 block">Razón de la devolución (opcional)</Label>
                <Input
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Ej: Producto defectuoso, Cliente cambió de opinión..."
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
              
              {/* Warning */}
              <div className="flex items-start gap-2 p-3 bg-amber-900/20 border border-amber-800/50 rounded-sm">
                <AlertTriangle size={16} className="text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-400">
                  <p className="font-medium">Esta acción no se puede deshacer.</p>
                  <p className="text-amber-400/70 mt-1">El stock se restaurará y el monto se deducirá de la contabilidad.</p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowRefundModal(false)}
              className="border-zinc-700 text-zinc-300"
              disabled={refundProcessing}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleRefundSale}
              disabled={refundProcessing}
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
            >
              {refundProcessing ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <>
                  <RotateCcw size={18} className="mr-2" />
                  Confirmar Devolución
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Sale Modal */}
      <Dialog open={!!editSale} onOpenChange={() => setEditSale(null)}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-md z-[150]">
          <DialogHeader>
            <DialogTitle className="text-white">Editar Venta</DialogTitle>
          </DialogHeader>
          {editSale && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Cliente</Label>
                <Input
                  value={editSale.customer_name || ''}
                  onChange={(e) => setEditSale({ ...editSale, customer_name: e.target.value })}
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Descuento (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={editSale.discount_percent || 0}
                  onChange={(e) => setEditSale({ ...editSale, discount_percent: parseFloat(e.target.value) || 0 })}
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Notas</Label>
                <Input
                  value={editSale.notes || ''}
                  onChange={(e) => setEditSale({ ...editSale, notes: e.target.value })}
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSale(null)} className="border-zinc-700">Cancelar</Button>
            <Button onClick={handleUpdateSale} className="bg-[#FF3B30] hover:bg-[#D32F2F]">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-white flex items-center justify-between">
              <span>{reportData?.title}</span>
              <div className="flex gap-2">
                <Button onClick={exportToExcel} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                  <FileSpreadsheet size={16} className="mr-1" /> Excel
                </Button>
                <Button onClick={exportToPDF} size="sm" className="bg-red-600 hover:bg-red-700">
                  <FileText size={16} className="mr-1" /> PDF
                </Button>
              </div>
            </DialogTitle>
            <p className="text-zinc-500 text-sm">Período: {reportData?.period}</p>
          </DialogHeader>

          <div className="flex-1 overflow-auto py-4">
            {reportData?.type === 'cierres' && reportData.data.sessions && (
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-3 text-sm">
                  <div className="bg-zinc-900 p-3 rounded">
                    <p className="text-zinc-500">Total Cierres</p>
                    <p className="text-xl font-bold text-white">{reportData.data.summary?.total_sessions || 0}</p>
                  </div>
                  <div className="bg-zinc-900 p-3 rounded">
                    <p className="text-zinc-500">Total Facturado</p>
                    <p className="text-xl font-bold text-emerald-400">{formatCurrency(reportData.data.summary?.total_sales)}</p>
                  </div>
                  <div className="bg-zinc-900 p-3 rounded">
                    <p className="text-zinc-500">Diferencias</p>
                    <p className="text-xl font-bold text-blue-400">{formatCurrency(reportData.data.summary?.total_differences)}</p>
                  </div>
                  <div className="bg-zinc-900 p-3 rounded">
                    <p className="text-zinc-500">Con Diferencia</p>
                    <p className="text-xl font-bold text-amber-400">{reportData.data.summary?.sessions_with_differences || 0}</p>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400">Usuario</TableHead>
                      <TableHead className="text-zinc-400">Fecha</TableHead>
                      <TableHead className="text-zinc-400">Apertura</TableHead>
                      <TableHead className="text-zinc-400">Cierre</TableHead>
                      <TableHead className="text-zinc-400 text-right">Facturado</TableHead>
                      <TableHead className="text-zinc-400 text-right">Diferencia</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.data.sessions.map(s => (
                      <TableRow key={s.id} className="border-zinc-800">
                        <TableCell className="text-white">{s.user_name}</TableCell>
                        <TableCell className="text-zinc-300">{formatDate(s.opened_at)}</TableCell>
                        <TableCell className="text-zinc-400">{formatTime(s.opened_at)}</TableCell>
                        <TableCell className="text-zinc-400">{formatTime(s.closed_at)}</TableCell>
                        <TableCell className="text-right text-emerald-400">{formatCurrency(s.total_sales)}</TableCell>
                        <TableCell className={`text-right ${s.difference >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
                          {s.difference >= 0 ? '+' : ''}{formatCurrency(s.difference)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {reportData?.type === 'ventas' && reportData.data.sales && (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead className="text-zinc-400">Fecha</TableHead>
                    <TableHead className="text-zinc-400">Cliente</TableHead>
                    <TableHead className="text-zinc-400">Vendedor</TableHead>
                    <TableHead className="text-zinc-400">Productos</TableHead>
                    <TableHead className="text-zinc-400">Método</TableHead>
                    <TableHead className="text-zinc-400 text-right">Total</TableHead>
                    {isAdmin() && <TableHead className="text-zinc-400 text-center">Acciones</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData.data.sales.map(s => (
                    <TableRow key={s.id} className={`border-zinc-800 ${s.refunded ? 'opacity-50' : ''}`}>
                      <TableCell className="text-zinc-300">{formatDateTime(s.created_at)}</TableCell>
                      <TableCell className="text-white">
                        {s.customer_name || '-'}
                        {s.refunded && <span className="ml-2 text-xs text-red-400">(Devuelta)</span>}
                      </TableCell>
                      <TableCell className="text-zinc-400">{s.seller_name}</TableCell>
                      <TableCell className="text-zinc-300 text-xs max-w-[280px]">
                        <div className="flex flex-col gap-0.5">
                          {(s.items || []).map((it, idx) => (
                            <span key={idx} className="truncate">
                              <span className="text-zinc-500">{it.quantity}×</span> {it.item_name}
                            </span>
                          ))}
                          {(!s.items || s.items.length === 0) && <span className="text-zinc-600">-</span>}
                        </div>
                      </TableCell>
                      <TableCell><PaymentMethodBadge sale={s} /></TableCell>
                      <TableCell className="text-right text-emerald-400">
                        {s.refunded ? <s className="text-zinc-500">{formatCurrency(s.total)}</s> : formatCurrency(s.total)}
                      </TableCell>
                      {isAdmin() && (
                        <TableCell className="text-center">
                          {!s.refunded && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openRefundModal(s)}
                              className="text-amber-400 hover:text-amber-300 h-7 w-7 p-0"
                              title="Devolver venta"
                            >
                              <RotateCcw size={14} />
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {reportData?.type === 'gastos' && reportData.data.expenses && (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead className="text-zinc-400">Fecha</TableHead>
                    <TableHead className="text-zinc-400">Tipo</TableHead>
                    <TableHead className="text-zinc-400">Categoría</TableHead>
                    <TableHead className="text-zinc-400">Descripción</TableHead>
                    <TableHead className="text-zinc-400 text-right">Monto</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData.data.expenses.map(e => (
                    <TableRow key={e.id} className="border-zinc-800">
                      <TableCell className="text-zinc-300">{formatDateTime(e.created_at)}</TableCell>
                      <TableCell className="text-white">{e.type === 'expense' ? 'Gasto' : 'Retiro'}</TableCell>
                      <TableCell className="text-zinc-400">{e.category}</TableCell>
                      <TableCell className="text-zinc-400">{e.description}</TableCell>
                      <TableCell className="text-right text-red-400">{formatCurrency(e.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          <DialogFooter className="flex-shrink-0">
            <Button variant="outline" onClick={() => setShowReportModal(false)} className="border-zinc-700">
              <X size={16} className="mr-2" /> Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Migration Result Modal */}
      <Dialog open={showMigrationResult} onOpenChange={setShowMigrationResult}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CheckCircle className="text-emerald-400" size={20} />
              Migración Completada
            </DialogTitle>
          </DialogHeader>
          
          {migrationResult && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-zinc-900 p-3 rounded">
                  <p className="text-zinc-500">Sesiones Cerradas</p>
                  <p className="text-xl font-bold text-white">{migrationResult.total_closed_sessions}</p>
                </div>
                <div className="bg-zinc-900 p-3 rounded">
                  <p className="text-zinc-500">Actualizadas</p>
                  <p className="text-xl font-bold text-emerald-400">{migrationResult.updated_count}</p>
                </div>
              </div>
              
              {migrationResult.sessions_with_changes > 0 && (
                <div className="bg-zinc-900 p-3 rounded">
                  <p className="text-zinc-500 text-sm mb-2">Sesiones con cambios:</p>
                  <div className="max-h-40 overflow-auto space-y-2">
                    {migrationResult.details?.map((d, i) => (
                      <div key={i} className="text-xs flex justify-between items-center border-b border-zinc-800 pb-1">
                        <span className="text-zinc-400">{d.opened_at}</span>
                        <span className="text-zinc-500">
                          {formatCurrency(d.old_total_sales)} → {formatCurrency(d.new_total_sales)}
                        </span>
                        <span className={d.change > 0 ? 'text-emerald-400' : 'text-red-400'}>
                          {d.change > 0 ? '+' : ''}{formatCurrency(d.change)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {migrationResult.sessions_with_changes === 0 && (
                <p className="text-zinc-400 text-center py-4">
                  ✓ Todas las sesiones ya tenían los datos correctos
                </p>
              )}
              
              {migrationResult.errors_count > 0 && (
                <div className="bg-red-900/20 border border-red-800 p-3 rounded">
                  <p className="text-red-400 text-sm">
                    {migrationResult.errors_count} errores durante la migración
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowMigrationResult(false)} className="bg-emerald-600 hover:bg-emerald-700">
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reports;
